import isEqual from 'lodash/isEqual'
import type { PresentationPreviewProps } from './PresentationImbedPreview'

/**
 * How long the loading screen stays up at minimum when `loadingStyle` is `'cv'`.
 *
 * This used to be 2600ms, which put a hard floor under every surface that embeds the preview —
 * the quote's Presentation tab and the Send dialog both pass `loadingStyle="cv"`, so neither
 * could paint in under 2.6 seconds however fast the iframe was ready (#22535). The floor is not
 * customer-facing: the real customer view renders `CustomerViewContents` directly and never
 * mounts this component. Its only job is to cover the iframe's first paint, which is what this
 * shorter value does.
 */
export const PRESENTATION_PREVIEW_MIN_LOADING_MS = 400

export interface PreviewLoadingArgs {
  loadingStyle: 'app' | 'cv' | 'none'
  /** the `PRESENTATION_PREVIEW_MIN_LOADING_MS` timer has fired */
  minDurationReached: boolean
  /** the iframe's fonts are not ready yet */
  contentLoading: boolean
  /** a teaser caller is still fetching its own options/quote */
  teaserLoading?: boolean
}

/**
 * Whether the loading screen covers the preview. Only the `'cv'` style waits on the minimum
 * duration and on a teaser's own loading state; `'app'` and `'none'` follow the font gate alone.
 */
export function shouldShowPreviewLoading(args: PreviewLoadingArgs): boolean {
  const { loadingStyle, minDurationReached, contentLoading, teaserLoading } = args

  if (loadingStyle === 'cv') {
    return !minDurationReached || contentLoading || !!teaserLoading
  }

  return contentLoading
}

/** Props compared by value rather than by reference — see `arePreviewPropsEqual`. */
const DEEP_COMPARED_PROPS = ['presentation', 'classes'] as const

/**
 * `React.memo` comparator for `PresentationImbedPreview`.
 *
 * Callers resolve the presentation in a render body and pass `classes` as an object literal, so a
 * reference compare never matches and the whole iframe re-renders on every keystroke of the
 * surrounding form (#22535). A deep compare of those two props is much cheaper than rebuilding the
 * iframe's JSS registry and re-rendering the customer view inside it.
 *
 * Everything else is compared by reference. That is deliberate for `teaserProps`: its `banner` is
 * a React node, and a React element carries an `_owner` fiber, so walking it deeply would cost far
 * more than the re-render it saves.
 */
export function arePreviewPropsEqual(prev: PresentationPreviewProps, next: PresentationPreviewProps): boolean {
  const keys = new Set([...Object.keys(prev), ...Object.keys(next)]) as Set<keyof PresentationPreviewProps>

  for (const key of keys) {
    if ((DEEP_COMPARED_PROPS as readonly string[]).includes(key)) {
      if (!isEqual(prev[key], next[key])) return false
    } else if (prev[key] !== next[key]) {
      return false
    }
  }

  return true
}
